import React from 'react';
import { DeviceEnrichment } from '../types';

/**
 * FS-1074 — what the MyFluxHive app holds about a device's owner, home,
 * system and filter, read-only, at the top of both device editors (the
 * Device List Edit dialog and the device Settings tab). Says so when the app
 * data could not be read or holds nothing for the device.
 */
interface DeviceAppUserDataProps {
  /** The device's app data, or undefined when the app has none for it. */
  enrichment: DeviceEnrichment | undefined;
  loading: boolean;
  error: string | null;
}

const appUserName = (enrichment: DeviceEnrichment): string =>
  [enrichment.user?.firstname, enrichment.user?.lastName].filter(Boolean).join(' ');

const hvacNames = (enrichment: DeviceEnrichment): string =>
  (enrichment.hvacSystems ?? []).map((h) => h.name).filter(Boolean).join(', ');

const filterSummary = (enrichment: DeviceEnrichment): string => {
  if (!enrichment.filter) return '';
  const { manufacturer, rating, length, width, thickness } = enrichment.filter;
  const parts: string[] = [];
  if (manufacturer) parts.push(manufacturer);
  if (length && width && thickness) parts.push(`${length}x${width}x${thickness}`);
  if (rating) parts.push(rating);
  return parts.join(' ');
};

const DeviceAppUserData: React.FC<DeviceAppUserDataProps> = ({ enrichment, loading, error }) => {
  if (loading) return null;
  if (error) return <p role="alert" className="mb-4 text-destructive">App user data could not be loaded. Device settings remain available.</p>;
  if (!enrichment) return <p className="mb-4 text-muted-foreground">No app user data was returned for this device. Device settings remain available.</p>;
  const name = appUserName(enrichment);
  return (
    <div className="mb-4 p-3 bg-green-50 border border-status-success-border rounded-sm text-sm">
      <p className="font-medium text-green-800 mb-1">App User Data (read-only)</p>
      <p>
        User: {enrichment.user?.email || name || 'N/A'}
        {enrichment.user?.email && name && (
          <span className="text-muted-foreground"> · {name}</span>
        )}
      </p>
      <p>Location: {enrichment.location?.name || 'N/A'}</p>
      <p>HVAC: {hvacNames(enrichment) || 'N/A'}</p>
      <p>Filter: {filterSummary(enrichment) || 'N/A'}</p>
    </div>
  );
};

export default DeviceAppUserData;
